import { ToolCall } from '@/types/agentChat';
import { CheckCircle, XmarkCircle } from 'iconoir-react';
import { AlertCircle, Loader2 } from 'lucide-react';

function formatUnknownToolName(name: string): string {
  // Convert snake_case or camelCase to Title Case
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

const toolNamesMap: Record<
  string,
  Record<'completed' | 'failed' | 'running' | 'stopped', string>
> = {
  smart_asset_search: {
    completed: 'Searched for Assets',
    failed: 'Failed to Find Assets',
    running: 'Searching for Assets...',
    stopped: 'Search Stopped',
  },
  smart_asset_search_with_urls: {
    completed: 'Searched for Assets',
    failed: 'Failed to Find Assets',
    running: 'Searching for Assets...',
    stopped: 'Search Stopped',
  },
  get_asset_with_url: {
    completed: 'Retrieved Asset',
    failed: 'Failed to Get Asset',
    running: 'Getting Asset...',
    stopped: 'Get Asset Stopped',
  },
  get_account_forms: {
    completed: 'Retrieved Forms',
    failed: 'Failed to Get Forms',
    running: 'Getting Forms...',
    stopped: 'Get Forms Stopped',
  },
  create_html_section: {
    completed: 'Created Section',
    failed: 'Failed to Create Section',
    running: 'Creating Section...',
    stopped: 'Create Section Stopped',
  },
  get_html_section: {
    completed: 'Retrieved Sections',
    failed: 'Failed to Get Sections',
    running: 'Getting Sections...',
    stopped: 'Get Sections Stopped',
  },
  update_html_section: {
    completed: 'Replaced Section',
    failed: 'Failed to Replace Section',
    running: 'Replacing Section...',
    stopped: 'Replace Section Stopped',
  },
  delete_section: {
    completed: 'Deleted Section',
    failed: 'Failed to Delete Section',
    running: 'Deleting Section...',
    stopped: 'Delete Section Stopped',
  },
  move_section: {
    completed: 'Moved Section',
    failed: 'Failed to Move Section',
    running: 'Moving Section...',
    stopped: 'Move Section Stopped',
  },
  update_html_section_replace: {
    completed: 'Updated Section',
    failed: 'Failed to Update Section',
    running: 'Updating Section...',
    stopped: 'Update Section Stopped',
  },
  update_html_section_metadata: {
    completed: 'Updated Section Metadata',
    failed: 'Failed to Update Section Metadata',
    running: 'Updating Section Metadata...',
    stopped: 'Update Metadata Stopped',
  },
  update_campaign_analysis: {
    completed: 'Analyzed Campaign Data',
    failed: 'Failed to Analyze Campaign Data',
    running: 'Analyzing Campaign Data...',
    stopped: 'Campaign Analysis Stopped',
  },
  search_campaign_assets: {
    completed: 'Searched Campaign Assets',
    failed: 'Failed to Search Campaign Assets',
    running: 'Searching Campaign Assets...',
    stopped: 'Search Campaign Assets Stopped',
  },
  get_campaign_ad_groups: {
    completed: 'Retrieved Ad Groups',
    failed: 'Failed to Get Ad Groups',
    running: 'Getting Ad Groups...',
    stopped: 'Get Ad Groups Stopped',
  },
  get_campaign_ads: {
    completed: 'Retrieved Ads',
    failed: 'Failed to Get Ads',
    running: 'Getting Ads...',
    stopped: 'Get Ads Stopped',
  },
  get_campaign_keywords: {
    completed: 'Retrieved Keywords',
    failed: 'Failed to Get Keywords',
    running: 'Getting Keywords...',
    stopped: 'Get Keywords Stopped',
  },
  create_simple_form: {
    completed: 'Form Created',
    failed: 'Failed to Create Form',
    running: 'Creating Form...',
    stopped: 'Create Form Stopped',
  },
  create_structure: {
    completed: 'Dynamic Page Structure Created',
    failed: 'Failed to Create Dynamic Page Structure',
    running: 'Creating Dynamic Page Structure...',
    stopped: 'Create Dynamic Page Structure Stopped',
  },
  remove_variant_from_structure: {
    completed: 'Variant Removed',
    failed: 'Failed to Remove Variant',
    running: 'Removing Variant...',
    stopped: 'Remove Variant Stopped',
  },
  add_variant_to_structure: {
    completed: 'Variant Added',
    failed: 'Failed to Add Variant',
    running: 'Adding Variant...',
    stopped: 'Add Variant Stopped',
  },
  update_variant_in_structure: {
    completed: 'Variant Updated',
    failed: 'Failed to Update Variant',
    running: 'Updating Variant...',
    stopped: 'Update Variant Stopped',
  },
  update_structure_prop: {
    completed: 'Dynamic Page Updated',
    failed: 'Failed to Update Dynamic Page',
    running: 'Updating Dynamic Page...',
    stopped: 'Update Dynamic Page Stopped',
  },
};

const getReadableToolName = (
  toolName: string,
  status: 'completed' | 'failed' | 'running' | 'stopped',
) => {
  const nameEntry = toolNamesMap[toolName];
  if (nameEntry?.[status]) {
    return nameEntry[status];
  }
  return formatUnknownToolName(toolName);
};

const getToolCallOutputMessage = (toolCall: ToolCall): string => {
  const output = toolCall.output as any;
  if (!output) return '';

  const outputContent = output.kwargs?.content;
  if (!outputContent) return '';

  try {
    const parsedContent = JSON.parse(outputContent);
    if (parsedContent?.userMessage) {
      if (
        parsedContent.userMessage === 'Invalid HTML provided for new section'
      ) {
        console.log('Parsed tool call output content', parsedContent);
      }
      return parsedContent.userMessage;
    }
    return '';
  } catch (e) {
    // Not JSON, return as is
    console.error('Error parsing tool call output content', e);
    return '';
  }
};

const getToolStatus = (
  toolCall: ToolCall,
): 'completed' | 'failed' | 'running' | 'stopped' => {
  if (toolCall.status === 'running') return 'running';
  if (toolCall.status === 'stopped') return 'stopped';
  const output = toolCall.output as any;
  if (!output) return 'running';

  const outputContent = output.kwargs?.content;
  if (!outputContent) return 'failed';

  if (typeof outputContent !== 'string') {
    return output.kwargs?.status === 'success' ? 'completed' : 'failed';
  }

  try {
    const parsedContent = JSON.parse(outputContent);
    if (parsedContent?.success === false) {
      return 'failed';
    }
    return 'completed';
  } catch (_) {
    return 'failed';
  }
};

interface ToolCallsRendererProps {
  toolCalls: ToolCall[];
}

export function ToolCallsRenderer({ toolCalls }: ToolCallsRendererProps) {
  if (!toolCalls || toolCalls.length === 0) return null;

  return (
    <div className="space-y-2">
      {toolCalls.map((toolCall) => {
        const toolOutputStatus = getToolStatus(toolCall);

        return (
          <div
            key={toolCall.id}
            className="relative flex min-h-10 max-w-92 items-center justify-between gap-2.5 rounded-xl bg-linear-to-br from-white/40 to-white px-3 py-2.5 text-sm"
            style={{
              border: '1px solid transparent',
              backgroundClip: 'padding-box',
            }}
          >
            <div
              className="pointer-events-none absolute inset-0 rounded-xl"
              style={{
                background:
                  'linear-gradient(95.57deg, rgba(255, 255, 255, 0.3) 10.18%, #FFFFFF 87.97%)',
                WebkitMask:
                  'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                WebkitMaskComposite: 'xor',
                mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                maskComposite: 'exclude',
                padding: '1px',
              }}
            />
            {toolOutputStatus === 'running' && (
              <Loader2 className="size-5 shrink-0 animate-spin text-blue-500" />
            )}
            {toolOutputStatus === 'completed' && (
              <CheckCircle className="size-5 shrink-0 text-green-500" />
            )}
            {toolOutputStatus === 'failed' && (
              <AlertCircle className="size-5 shrink-0 text-red-500" />
            )}
            {toolOutputStatus === 'stopped' && (
              <XmarkCircle className="size-5 shrink-0 text-orange-500" />
            )}

            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-base-900 text-sm leading-none font-medium tracking-tight">
                {getReadableToolName(toolCall.toolName, toolOutputStatus)}
              </span>
              {getToolCallOutputMessage(toolCall) && (
                <span className="text-base-500 text-xs leading-tight font-normal tracking-tight">
                  {getToolCallOutputMessage(toolCall)}
                </span>
              )}
            </div>

            {!!toolCall.duration && toolCall.duration > 50 && (
              <span className="text-base-400 shrink-0 text-sm leading-none font-normal tracking-tight">
                {(toolCall.duration / 1000).toFixed(2)}s
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
